import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  isAndroid,
  isIOS,
  isRealMobileDevice,
  shouldUseMediaRecorderFallback,
  supportsMediaRecorderCapture,
  supportsWebSpeechRecognition,
  markWebSpeechUnreliable,
  clearWebSpeechUnreliable,
  markServerTranscriptionUnavailable,
  clearServerTranscriptionUnavailable,
} from '../utils/voiceCaptureSupport';
import {
  transcribeAudioBlob,
  createMediaRecorder,
  isTranscriptionUnavailableError,
  resolveVoiceLanguageCode,
  requestMicrophoneAccess,
  getSharedAudioContext,
  getSharedMicrophoneStream,
  releaseSharedMicrophoneStream,
  formatVoiceTranscriptionError,
  VoiceTranscriptionError,
} from '../utils/mobileAudioCapture';
import { VOICE_TIMING } from '../voice/constants';
import { isVoiceCaptureUiDisabled } from '../voice/killSwitches';
import { resolveSpeechRecognitionLocale } from '../voice/resolveVoiceLanguage';
import { getSharedTtsManager } from '../voice/ttsManager';
import { createVoiceCommandId } from '../utils/voiceCommandClient';
import { emitVoiceCaptureUiState } from '../hooks/useVoiceUiPhase';
import { reportVoiceTelemetry } from '../utils/voiceTelemetryClient';
import type { CaptureDisposition, CaptureStopReason } from '../voice/types';

// Speech Recognition API types
interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  abort(): void;
  onstart: (() => void) | null;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onend: (() => void) | null;
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
}

interface SpeechRecognitionEvent {
  resultIndex: number;
  results: SpeechRecognitionResultList;
}

interface SpeechRecognitionResultList {
  length: number;
  item(index: number): SpeechRecognitionResult;
  [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionResult {
  length: number;
  item(index: number): SpeechRecognitionAlternative;
  [index: number]: SpeechRecognitionAlternative;
  isFinal: boolean;
}

interface SpeechRecognitionAlternative {
  transcript: string;
  confidence: number;
}

interface SpeechRecognitionErrorEvent {
  error: string;
  message: string;
}

interface GlobalVoiceInputHandlerProps {
  onTranscription: (transcript: string) => void;
  onProcessingChange: (isProcessing: boolean) => void;
}

// How long to wait after the user pauses mid-sentence.
const SPEECH_SILENCE_TIMEOUT_MS = VOICE_TIMING.POST_SPEECH_SILENCE_MS;
// Hard cap so recording cannot run indefinitely.
const MAX_RECORDING_MS = VOICE_TIMING.MAX_RECORDING_MS;
// Initial no-speech window before auto-stop.
const INITIAL_SILENCE_TIMEOUT_MS = VOICE_TIMING.INITIAL_SILENCE_MS;
// Volume threshold for detecting speech during MediaRecorder capture.
const SPEECH_VOLUME_THRESHOLD = 0.08;
const MOBILE_SPEECH_VOLUME_THRESHOLD = 0.055;
const ANDROID_SPEECH_VOLUME_THRESHOLD = 0.035;
// Hysteresis band: ambient noise often sits between silence and speech levels.
const SILENCE_VOLUME_RATIO = 0.55;
// While the user is speaking, extend the pause timer at most this often.
const SPEECH_PAUSE_RESET_MS = 3000;

const getSpeechVolumeThreshold = (): number => {
  if (isAndroid()) return ANDROID_SPEECH_VOLUME_THRESHOLD;
  if (isRealMobileDevice()) return MOBILE_SPEECH_VOLUME_THRESHOLD;
  return SPEECH_VOLUME_THRESHOLD;
};

const getSilenceVolumeThreshold = (): number => (
  getSpeechVolumeThreshold() * SILENCE_VOLUME_RATIO
);

const getMediaRecorderTimesliceMs = (): number | undefined => {
  // iOS Safari and many Android devices (Samsung, etc.) emit empty timeslice
  // chunks — record continuously and flush via requestData().
  if (isIOS() || isAndroid()) return undefined;
  return 250;
};

const resolveSpeechLocale = (): string => resolveSpeechRecognitionLocale();

const GlobalVoiceInputHandler: React.FC<GlobalVoiceInputHandlerProps> = ({
  onTranscription,
  onProcessingChange
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const finalTranscriptRef = useRef<string>('');
  const isListeningRef = useRef(false);
  const stopRequestedRef = useRef(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const usingMediaRecorderRef = useRef(false);
  const mediaRecorderFallbackAttemptedRef = useRef(false);
  const startInProgressRef = useRef(false);
  const previewTranscribingRef = useRef(false);
  const previewTranscriptRef = useRef('');
  const previewIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const iosDataFlushIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const androidDataFlushIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Web Audio analysis for the live waveform
  const audioContextRef = useRef<AudioContext | null>(null);
  const usingSharedAudioContextRef = useRef(false);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const rafRef = useRef<number | null>(null);

  // Silence detection state
  const silenceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const maxRecordingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasSpeechRef = useRef<boolean>(false);
  const recordingStartedAtRef = useRef<number>(0);
  const wasAboveSpeechThresholdRef = useRef(false);
  const lastVolumeResetAtRef = useRef(0);
  /** When true, MediaRecorder finalization must discard (never submit/transcribe). */
  const discardCaptureRef = useRef(false);
  const captureIdRef = useRef<string | null>(null);
  const captureGenerationRef = useRef(0);

  const onTranscriptionRef = useRef(onTranscription);
  const onProcessingChangeRef = useRef(onProcessingChange);
  const createRecognitionSessionRef = useRef<() => SpeechRecognition | null>(() => null);

  useEffect(() => {
    onTranscriptionRef.current = onTranscription;
    onProcessingChangeRef.current = onProcessingChange;
  }, [onTranscription, onProcessingChange]);

  const clearSilenceTimeout = useCallback(() => {
    if (silenceTimeoutRef.current) {
      clearTimeout(silenceTimeoutRef.current);
      silenceTimeoutRef.current = null;
    }
  }, []);

  const clearRecordingTimers = useCallback(() => {
    clearSilenceTimeout();
    if (maxRecordingTimeoutRef.current) {
      clearTimeout(maxRecordingTimeoutRef.current);
      maxRecordingTimeoutRef.current = null;
    }
  }, [clearSilenceTimeout]);

  const clearPreviewTranscription = useCallback(() => {
    if (previewIntervalRef.current) {
      clearInterval(previewIntervalRef.current);
      previewIntervalRef.current = null;
    }
    if (iosDataFlushIntervalRef.current) {
      clearInterval(iosDataFlushIntervalRef.current);
      iosDataFlushIntervalRef.current = null;
    }
    if (androidDataFlushIntervalRef.current) {
      clearInterval(androidDataFlushIntervalRef.current);
      androidDataFlushIntervalRef.current = null;
    }
    previewTranscribingRef.current = false;
    previewTranscriptRef.current = '';
  }, []);

  const getSilenceTimeoutMs = useCallback(() => (
    hasSpeechRef.current ? SPEECH_SILENCE_TIMEOUT_MS : INITIAL_SILENCE_TIMEOUT_MS
  ), []);

  const releaseMicrophoneStream = useCallback((options?: { force?: boolean }) => {
    const force = options?.force === true;
    // On iOS, keep the live stream between captures — stopping tracks and
    // re-calling getUserMedia re-prompts (or fails outside a user gesture).
    if (isIOS() && !force) {
      return;
    }
    if (mediaStreamRef.current) {
      // Only stop tracks if this isn't the shared iOS stream (unless forced).
      const shared = getSharedMicrophoneStream();
      if (force || mediaStreamRef.current !== shared) {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      }
      mediaStreamRef.current = null;
    }
    if (force) {
      releaseSharedMicrophoneStream();
    }
  }, []);

  const ensureMicrophoneStream = useCallback(async (): Promise<MediaStream | null> => {
    const stream = await requestMicrophoneAccess(
      mediaStreamRef.current || getSharedMicrophoneStream()
    );
    if (stream) {
      mediaStreamRef.current = stream;
    }
    return stream;
  }, []);

  const stopWaveform = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (audioContextRef.current) {
      if (!usingSharedAudioContextRef.current) {
        audioContextRef.current.close().catch(() => {});
      }
      audioContextRef.current = null;
      usingSharedAudioContextRef.current = false;
    }
    analyserRef.current = null;
    window.dispatchEvent(new CustomEvent('voiceWaveform', { detail: { levels: [], volume: 0 } }));
  }, []);

  const startWaveform = useCallback(async (existingStream?: MediaStream | null) => {
    try {
      if (audioContextRef.current) return;

      const stream = existingStream || mediaStreamRef.current || await ensureMicrophoneStream();
      if (!stream) return;

      const win = window as Window & { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext };
      const AudioCtx = win.AudioContext || win.webkitAudioContext;
      if (!AudioCtx) return;

      const shared = getSharedAudioContext();
      const audioContext: AudioContext = shared ?? new AudioCtx();
      usingSharedAudioContextRef.current = Boolean(shared);
      if (audioContext.state === 'suspended') {
        await audioContext.resume();
      }
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.8;
      source.connect(analyser);

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;

      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      const timeDomainArray = new Uint8Array(bufferLength);
      const BAR_COUNT = 28;

      const tick = () => {
        if (!analyserRef.current) return;

        if (audioContextRef.current?.state === 'suspended') {
          void audioContextRef.current.resume().catch(() => {});
        }

        analyserRef.current.getByteFrequencyData(dataArray);
        analyserRef.current.getByteTimeDomainData(timeDomainArray);

        const levels: number[] = new Array(BAR_COUNT);
        const step = Math.floor(bufferLength / BAR_COUNT) || 1;
        let sum = 0;
        for (let i = 0; i < BAR_COUNT; i++) {
          const v = dataArray[i * step] / 255;
          levels[i] = v;
          sum += v;
        }

        let sumSquares = 0;
        for (let i = 0; i < timeDomainArray.length; i++) {
          const normalized = (timeDomainArray[i] - 128) / 128;
          sumSquares += normalized * normalized;
        }
        const rms = Math.sqrt(sumSquares / timeDomainArray.length);
        const volume = Math.max(sum / BAR_COUNT, rms);

        if (usingMediaRecorderRef.current && isListeningRef.current) {
          const speechThreshold = getSpeechVolumeThreshold();
          const silenceThreshold = getSilenceVolumeThreshold();

          if (volume > speechThreshold) {
            if (!hasSpeechRef.current) {
              hasSpeechRef.current = true;
              emitVoiceCaptureUiState({ phase: 'speech-detected', silenceEndsAt: null });
              window.dispatchEvent(new CustomEvent('transcriptUpdate', {
                detail: { transcript: '…', isFinal: false },
              }));
            }
            const now = Date.now();
            const risingEdge = !wasAboveSpeechThresholdRef.current;
            const sustainedSpeech = now - lastVolumeResetAtRef.current >= SPEECH_PAUSE_RESET_MS;
            if (risingEdge || sustainedSpeech) {
              lastVolumeResetAtRef.current = now;
              resetSilenceDetectionRef.current();
            }
            wasAboveSpeechThresholdRef.current = true;
          } else if (volume < silenceThreshold) {
            wasAboveSpeechThresholdRef.current = false;
          }
        }

        window.dispatchEvent(new CustomEvent('voiceWaveform', { detail: { levels, volume } }));
        rafRef.current = requestAnimationFrame(tick);
      };
      tick();
    } catch (err) {
      console.warn('Waveform analysis unavailable:', err);
    }
  }, [ensureMicrophoneStream]);

  const stopRecognition = useCallback((options?: {
    disposition?: CaptureDisposition;
    reason?: CaptureStopReason;
  }) => {
    startInProgressRef.current = false;
    stopRequestedRef.current = true;
    captureGenerationRef.current += 1;

    const disposition = options?.disposition;
    const reason = options?.reason;
    if (disposition === 'discard' || reason === 'navigation' || reason === 'unmount' || reason === 'user-cancel' || reason === 'logout' || reason === 'business-switch' || reason === 'superseded' || reason === 'permission-cancelled') {
      discardCaptureRef.current = true;
    } else if (disposition === 'submit') {
      discardCaptureRef.current = false;
    }

    clearRecordingTimers();
    clearPreviewTranscription();

    if (usingMediaRecorderRef.current && mediaRecorderRef.current) {
      if (mediaRecorderRef.current.state !== 'inactive') {
        try {
          if (typeof mediaRecorderRef.current.requestData === 'function') {
            mediaRecorderRef.current.requestData();
          }
          mediaRecorderRef.current.stop();
        } catch {
          /* already stopped */
        }
      } else if (discardCaptureRef.current) {
        // Recorder already inactive — still clear chunks on discard.
        audioChunksRef.current = [];
        previewTranscriptRef.current = '';
        usingMediaRecorderRef.current = false;
        isListeningRef.current = false;
        releaseMicrophoneStream();
        window.dispatchEvent(new CustomEvent('recordingStateChange', { detail: false }));
      }
      return;
    }

    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch {
        /* already stopped */
      }
    }
  }, [clearPreviewTranscription, clearRecordingTimers, releaseMicrophoneStream]);

  const scheduleMaxRecordingTimeout = useCallback(() => {
    if (maxRecordingTimeoutRef.current || !recordingStartedAtRef.current) return;
    const elapsed = Date.now() - recordingStartedAtRef.current;
    const remaining = Math.max(MAX_RECORDING_MS - elapsed, 1000);
    maxRecordingTimeoutRef.current = setTimeout(() => {
      if (!isListeningRef.current) return;
      console.log('⏰ Max recording duration reached, stopping recording');
      stopRecognition();
    }, remaining);
  }, [stopRecognition]);

  const startSilenceDetection = useCallback(() => {
    clearSilenceTimeout();
    const timeoutMs = getSilenceTimeoutMs();
    const silenceEndsAt = Date.now() + timeoutMs;

    if (hasSpeechRef.current && isListeningRef.current) {
      emitVoiceCaptureUiState({
        phase: 'waiting-for-finish',
        silenceEndsAt,
      });
    }

    silenceTimeoutRef.current = setTimeout(() => {
      if (!isListeningRef.current) return;
      console.log(
        hasSpeechRef.current
          ? '🔇 Pause detected after speech, stopping recording'
          : '🔇 No speech detected within listening window, stopping recording'
      );
      window.dispatchEvent(new CustomEvent('silenceDetected'));
      emitVoiceCaptureUiState({ phase: 'finishing' });
      stopRecognition();
    }, timeoutMs);
  }, [clearSilenceTimeout, getSilenceTimeoutMs, stopRecognition]);

  const resetSilenceDetectionRef = useRef<() => void>(() => {});

  const resetSilenceDetection = useCallback(() => {
    if (!isListeningRef.current) return;
    if (hasSpeechRef.current) {
      emitVoiceCaptureUiState({ phase: 'speech-detected', silenceEndsAt: null });
    }
    startSilenceDetection();
  }, [startSilenceDetection]);

  useEffect(() => {
    resetSilenceDetectionRef.current = resetSilenceDetection;
  }, [resetSilenceDetection]);

  const finishMediaRecorderCapture = useCallback(async () => {
    startInProgressRef.current = false;
    usingMediaRecorderRef.current = false;
    isListeningRef.current = false;
    const captureStartedAt = recordingStartedAtRef.current;
    const hadSpeech = hasSpeechRef.current;
    recordingStartedAtRef.current = 0;
    stopWaveform();
    clearRecordingTimers();
    clearPreviewTranscription();
    window.dispatchEvent(new CustomEvent('recordingStateChange', { detail: false }));

    const shouldDiscard = discardCaptureRef.current;
    discardCaptureRef.current = false;
    const chunks = audioChunksRef.current;
    audioChunksRef.current = [];
    previewTranscriptRef.current = '';
    mediaRecorderRef.current = null;
    releaseMicrophoneStream();
    const durationMs = captureStartedAt ? Date.now() - captureStartedAt : 0;

    if (shouldDiscard) {
      emitVoiceCaptureUiState({ phase: 'idle' });
      reportVoiceTelemetry('VOICE_CAPTURE_FINALIZED', {
        capture_id: captureIdRef.current,
        disposition: 'discard',
        reason: 'user-cancel',
        duration_ms: durationMs,
        had_speech: hadSpeech,
      });
      onProcessingChangeRef.current(false);
      return;
    }

    if (!chunks.length) {
      onProcessingChangeRef.current(false);
      const message = 'No audio was captured. Please check your microphone and try again.';
      emitVoiceCaptureUiState({ phase: 'idle', alertMessage: message });
      window.dispatchEvent(new CustomEvent('voiceMicError', {
        detail: { message },
      }));
      return;
    }

    const mimeType = chunks[0]?.type || 'audio/webm';
    const blob = new Blob(chunks, { type: mimeType });

    if (blob.size < 256) {
      onProcessingChangeRef.current(false);
      window.dispatchEvent(new CustomEvent('voiceMicError', {
        detail: {
          message: 'No audio was captured. Please check your microphone and try again.',
        },
      }));
      return;
    }

    const voiceLanguage = resolveVoiceLanguageCode();
    let transcriptionDispatched = false;

    try {
      setIsProcessing(true);
      onProcessingChangeRef.current(true);
      emitVoiceCaptureUiState({ phase: 'transcribing' });
      reportVoiceTelemetry('VOICE_CAPTURE_FINALIZED', {
        capture_id: captureIdRef.current,
        disposition: 'submit',
        reason: 'user-done',
        duration_ms: durationMs,
        audio_bytes: blob.size,
        had_speech: hadSpeech,
      });
      const text = await transcribeAudioBlob(blob, voiceLanguage);
      if (text.length > 1) {
        transcriptionDispatched = true;
        clearServerTranscriptionUnavailable();
        emitVoiceCaptureUiState({ phase: 'understanding' });
        window.dispatchEvent(new CustomEvent('transcriptUpdate', {
          detail: {
            transcript: text,
            isFinal: true,
            capture_id: captureIdRef.current,
          },
        }));
        onTranscriptionRef.current(text);
      } else {
        const message = 'No speech detected in the recording. Please try again.';
        emitVoiceCaptureUiState({ phase: 'idle', alertMessage: message });
        window.dispatchEvent(new CustomEvent('voiceMicError', {
          detail: { message },
        }));
      }
    } catch (error) {
      const rawMessage = error instanceof Error ? error.message : 'Transcription failed';
      const displayMessage = error instanceof VoiceTranscriptionError
        ? formatVoiceTranscriptionError(error.details)
        : rawMessage;
      console.warn('Media recorder transcription failed:', error);

      // Preferred strategy: do not execute from unverified preview transcript.
      // Show retry instead of dead Web Speech fallback on mobile / preview text.
      if (isTranscriptionUnavailableError(rawMessage) && isRealMobileDevice()) {
        markServerTranscriptionUnavailable();
      }

      emitVoiceCaptureUiState({ phase: 'idle', alertMessage: displayMessage });
      window.dispatchEvent(new CustomEvent('voiceMicError', {
        detail: { message: displayMessage },
      }));
    } finally {
      setIsProcessing(false);
      if (!transcriptionDispatched) {
        onProcessingChangeRef.current(false);
      }
    }
  }, [clearPreviewTranscription, clearRecordingTimers, releaseMicrophoneStream, stopWaveform]);

  const startMediaRecorderCapture = useCallback(async (stream: MediaStream) => {
    audioChunksRef.current = [];
    usingMediaRecorderRef.current = true;
    stopRequestedRef.current = false;
    recordingStartedAtRef.current = Date.now();
    hasSpeechRef.current = false;
    finalTranscriptRef.current = '';
    previewTranscriptRef.current = '';
    wasAboveSpeechThresholdRef.current = false;
    lastVolumeResetAtRef.current = 0;

    const recorder = createMediaRecorder(stream, (chunk) => {
      audioChunksRef.current.push(chunk);
    });

    recorder.addEventListener('stop', () => {
      void finishMediaRecorderCapture();
    });

    mediaRecorderRef.current = recorder;
    const timeslice = getMediaRecorderTimesliceMs();
    if (timeslice) {
      recorder.start(timeslice);
    } else {
      recorder.start();
    }
    isListeningRef.current = true;
    startInProgressRef.current = false;

    // iOS and Android need explicit requestData() flushes when recording without timeslice.
    if ((isIOS() || isAndroid()) && !timeslice) {
      const flushIntervalRef = isIOS() ? iosDataFlushIntervalRef : androidDataFlushIntervalRef;
      flushIntervalRef.current = setInterval(() => {
        if (!isListeningRef.current || !mediaRecorderRef.current) return;
        if (mediaRecorderRef.current.state !== 'recording') return;
        try {
          mediaRecorderRef.current.requestData();
        } catch {
          /* ignore */
        }
      }, isIOS() ? 1500 : 1000);
    }

    await startWaveform(stream);
    startSilenceDetection();
    scheduleMaxRecordingTimeout();
    emitVoiceCaptureUiState({ phase: 'listening' });
    window.dispatchEvent(new CustomEvent('recordingStateChange', { detail: true }));
    reportVoiceTelemetry('VOICE_CAPTURE_STARTED', {
      capture_id: captureIdRef.current,
      source: 'microphone',
    });
  }, [finishMediaRecorderCapture, scheduleMaxRecordingTimeout, startSilenceDetection, startWaveform]);

  const createRecognitionSession = useCallback(() => {
    if (!supportsWebSpeechRecognition()) return null;

    if (recognitionRef.current) {
      try {
        recognitionRef.current.abort();
      } catch {
        try {
          recognitionRef.current.stop();
        } catch {
          /* already stopped */
        }
      }
      recognitionRef.current = null;
    }

    const SpeechRecognitionCtor = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    const recognition = new SpeechRecognitionCtor() as SpeechRecognition;

    recognition.continuous = !isRealMobileDevice();
    recognition.interimResults = true;
    recognition.lang = resolveSpeechLocale();

    recognition.onstart = () => {
      console.log('🎤 Global voice input started');
      startInProgressRef.current = false;
      isListeningRef.current = true;
      stopRequestedRef.current = false;
      if (!recordingStartedAtRef.current) {
        recordingStartedAtRef.current = Date.now();
      }
      hasSpeechRef.current = false;
      finalTranscriptRef.current = '';
      wasAboveSpeechThresholdRef.current = false;
      lastVolumeResetAtRef.current = 0;

      void startWaveform(mediaStreamRef.current);
      startSilenceDetection();
      scheduleMaxRecordingTimeout();
      emitVoiceCaptureUiState({ phase: 'listening' });
      window.dispatchEvent(new CustomEvent('recordingStateChange', { detail: true }));
      reportVoiceTelemetry('VOICE_CAPTURE_STARTED', {
        capture_id: captureIdRef.current,
        source: 'microphone',
      });
    };

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let finalTranscript = '';
      let interimTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }

      if (finalTranscript.trim() || interimTranscript.trim()) {
        hasSpeechRef.current = true;
        emitVoiceCaptureUiState({ phase: 'speech-detected', silenceEndsAt: null });
        resetSilenceDetection();
      } else if (hasSpeechRef.current) {
        resetSilenceDetection();
      }

      if (finalTranscript) {
        finalTranscriptRef.current = `${finalTranscriptRef.current} ${finalTranscript}`.replace(/\s+/g, ' ').trim();
        console.log('📝 Global voice input final transcript:', finalTranscriptRef.current);
      }

      const currentTranscript = `${finalTranscriptRef.current} ${interimTranscript}`.replace(/\s+/g, ' ').trim();
      if (currentTranscript) {
        window.dispatchEvent(new CustomEvent('transcriptUpdate', {
          detail: {
            transcript: currentTranscript,
            isFinal: finalTranscript.length > 0
          }
        }));
      }
    };

    recognition.onend = () => {
      // We switched to MediaRecorder capture mid-session (Web Speech service
      // error); that capture owns the stream and timers now.
      if (usingMediaRecorderRef.current) return;

      const elapsed = recordingStartedAtRef.current
        ? Date.now() - recordingStartedAtRef.current
        : 0;
      // Android Chrome (continuous = false) often ends the session after a few
      // quiet seconds; silently restart while we are still inside the initial
      // listening window so the mic does not appear to cut out.
      // Mobile Web Speech sessions end quickly (continuous=false). Restarting
      // them while still waiting for speech caused endless "Listening…" loops.
      const shouldRetryForSpeech = !isRealMobileDevice()
        && !stopRequestedRef.current
        && !hasSpeechRef.current
        && !finalTranscriptRef.current.trim()
        && elapsed > 0
        && elapsed < INITIAL_SILENCE_TIMEOUT_MS;

      if (shouldRetryForSpeech && recognitionRef.current) {
        console.log('🔄 Restarting recognition — still waiting for you to speak');
        try {
          recognitionRef.current.start();
          resetSilenceDetection();
          return;
        } catch (error) {
          console.warn('Could not restart recognition while waiting for speech:', error);
        }
      }

      console.log('🎤 Voice input ended');
      startInProgressRef.current = false;
      isListeningRef.current = false;
      recordingStartedAtRef.current = 0;
      stopWaveform();
      releaseMicrophoneStream();
      clearRecordingTimers();
      clearPreviewTranscription();
      onProcessingChangeRef.current(false);
      window.dispatchEvent(new CustomEvent('recordingStateChange', { detail: false }));

      const currentTranscript = finalTranscriptRef.current.trim();
      finalTranscriptRef.current = '';

      if (currentTranscript && currentTranscript.length > 1) {
        console.log('🎤 Processing voice input:', currentTranscript);
        clearWebSpeechUnreliable();
        clearServerTranscriptionUnavailable();
        setIsProcessing(true);
        onProcessingChangeRef.current(true);
        onTranscriptionRef.current(currentTranscript);
      } else {
        console.log('🎤 No speech detected, ignoring');
        setIsProcessing(false);
        onProcessingChangeRef.current(false);
      }
    };

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      if (event.error === 'no-speech' && !hasSpeechRef.current && !stopRequestedRef.current) {
        console.log('🔇 Browser reported no speech yet — keeping the mic open');
        resetSilenceDetection();
        return;
      }

      // Web Speech service failures (e.g. Android devices without Google
      // services, blocked speech servers): switch to MediaRecorder + server
      // Whisper in the same capture instead of dropping the command.
      const isServiceError = ['network', 'service-not-allowed', 'language-not-supported'].includes(event.error);
      if (isServiceError) {
        markWebSpeechUnreliable();
      }
      if (
        isServiceError
        && !stopRequestedRef.current
        && !mediaRecorderFallbackAttemptedRef.current
        && supportsMediaRecorderCapture()
        && mediaStreamRef.current?.active
      ) {
        console.warn(`⚠️ Web Speech failed (${event.error}) — switching to server transcription`);
        mediaRecorderFallbackAttemptedRef.current = true;
        recognitionRef.current = null;
        clearRecordingTimers();
        void startMediaRecorderCapture(mediaStreamRef.current).catch((fallbackError) => {
          console.warn('MediaRecorder fallback after Web Speech error failed:', fallbackError);
        });
        return;
      }

      console.warn('⚠️ Global voice input error:', event.error);
      startInProgressRef.current = false;
      isListeningRef.current = false;
      recordingStartedAtRef.current = 0;
      stopWaveform();
      releaseMicrophoneStream();
      clearRecordingTimers();
      clearPreviewTranscription();
      onProcessingChangeRef.current(false);
      window.dispatchEvent(new CustomEvent('recordingStateChange', { detail: false }));

      const errorMessages: Record<string, string> = {
        'not-allowed': 'Microphone access denied. Please allow microphone access in your browser settings.',
        'audio-capture': 'No microphone was found. Please check that a microphone is connected and try again.',
        network: 'Speech recognition needs a network connection. Please check your connection, or type your message.',
        'service-not-allowed': 'Speech recognition is unavailable on this device. Please type your message instead.',
        'language-not-supported': 'This language is not supported by your browser\u2019s speech recognition. Please type your message.',
      };
      const userMessage = errorMessages[event.error];
      if (userMessage && !stopRequestedRef.current) {
        window.dispatchEvent(new CustomEvent('voiceMicError', { detail: { message: userMessage } }));
      }
    };

    recognitionRef.current = recognition;
    return recognition;
  }, [clearPreviewTranscription, clearRecordingTimers, releaseMicrophoneStream, resetSilenceDetection, scheduleMaxRecordingTimeout, startSilenceDetection, startWaveform, stopWaveform]);

  useEffect(() => {
    createRecognitionSessionRef.current = createRecognitionSession;
  }, [createRecognitionSession]);

  useEffect(() => () => {
    stopRecognition();
    recognitionRef.current = null;
    clearRecordingTimers();
    clearPreviewTranscription();
    stopWaveform();
    releaseMicrophoneStream({ force: true });
  }, [clearPreviewTranscription, clearRecordingTimers, releaseMicrophoneStream, stopRecognition, stopWaveform]);

  // Listen for global voice input events — single mounted owner (V3-CLIENT-INTEGRATION).
  useEffect(() => {
    const handleStartVoiceInput = async (event: Event) => {
      console.log('🎤 Start voice input event received');
      if (isVoiceCaptureUiDisabled()) {
        return;
      }
      if (isListeningRef.current || startInProgressRef.current) {
        // A second mic press while recording means "stop" (Done) — never queue a
        // restart, which produced an endless stop/start recording loop on
        // mobile when the UI's recording state was momentarily stale.
        if (isListeningRef.current) {
          stopRecognition({ disposition: 'submit', reason: 'user-done' });
        }
        return;
      }
      startInProgressRef.current = true;
      discardCaptureRef.current = false;
      captureGenerationRef.current += 1;
      const startGeneration = captureGenerationRef.current;

      const detail = (event as CustomEvent<{ stream?: MediaStream | null; capture_id?: string }>).detail;
      captureIdRef.current = detail?.capture_id || createVoiceCommandId();

      recordingStartedAtRef.current = 0;
      hasSpeechRef.current = false;
      finalTranscriptRef.current = '';
      wasAboveSpeechThresholdRef.current = false;
      lastVolumeResetAtRef.current = 0;
      stopRequestedRef.current = false;
      mediaRecorderFallbackAttemptedRef.current = false;
      window.dispatchEvent(new CustomEvent('transcriptUpdate', {
        detail: { transcript: '', isFinal: false, capture_id: captureIdRef.current },
      }));

      emitVoiceCaptureUiState({
        phase: (detail?.stream?.active || getSharedMicrophoneStream())
          ? 'listening'
          : 'requesting-permission',
      });

      // Prefer an already-granted stream before any async work so iOS does
      // not lose the user-gesture window for getUserMedia.
      const prewarmed = detail?.stream;
      let stream = prewarmed?.active
        ? prewarmed
        : getSharedMicrophoneStream();
      if (!stream) {
        stream = await ensureMicrophoneStream();
      }

      if (captureGenerationRef.current !== startGeneration || stopRequestedRef.current) {
        startInProgressRef.current = false;
        return;
      }

      if (stream) {
        mediaStreamRef.current = stream;
      }

      try {
        await getSharedTtsManager().prepareForCapture();
      } catch {
        /* never block capture forever on TTS errors */
      }

      if (captureGenerationRef.current !== startGeneration || stopRequestedRef.current) {
        startInProgressRef.current = false;
        return;
      }

      if (!stream) {
        startInProgressRef.current = false;
        const message = 'Microphone access denied. Please allow microphone access in your browser settings.';
        emitVoiceCaptureUiState({ phase: 'idle', alertMessage: message });
        window.dispatchEvent(new CustomEvent('recordingStateChange', { detail: false }));
        window.dispatchEvent(new CustomEvent('voiceMicError', {
          detail: { message },
        }));
        return;
      }

      if (shouldUseMediaRecorderFallback()) {
        try {
          await startMediaRecorderCapture(stream);
        } catch (error) {
          console.warn('Could not start media recorder capture:', error);
          startInProgressRef.current = false;
          window.dispatchEvent(new CustomEvent('recordingStateChange', { detail: false }));
          releaseMicrophoneStream();
          window.dispatchEvent(new CustomEvent('voiceMicError', {
            detail: {
              message: 'Could not start voice recording. Please check microphone permissions and try again.',
            },
          }));
        }
        return;
      }

      const recognition = createRecognitionSession();
      if (!recognition) {
        startInProgressRef.current = false;
        window.dispatchEvent(new CustomEvent('recordingStateChange', { detail: false }));
        window.dispatchEvent(new CustomEvent('voiceMicError', {
          detail: { message: 'Speech recognition is not supported on this device.' },
        }));
        releaseMicrophoneStream();
        return;
      }

      try {
        recognition.start();
      } catch (error) {
        console.warn('Could not start recognition (may already be active):', error);
        const retryRecognition = createRecognitionSession();
        if (!retryRecognition) {
          startInProgressRef.current = false;
          window.dispatchEvent(new CustomEvent('recordingStateChange', { detail: false }));
          releaseMicrophoneStream();
          return;
        }

        setTimeout(() => {
          if (!recognitionRef.current || isListeningRef.current || stopRequestedRef.current) {
            startInProgressRef.current = false;
            return;
          }
          try {
            recognitionRef.current.start();
          } catch (retryError) {
            console.warn('Recognition retry failed:', retryError);
            startInProgressRef.current = false;
            window.dispatchEvent(new CustomEvent('recordingStateChange', { detail: false }));
            releaseMicrophoneStream();
          }
        }, 120);
      }
    };

    const handleStopVoiceInput = (event: Event) => {
      console.log('🎤 Stop voice input event received');
      const detail = (event as CustomEvent<{ disposition?: CaptureDisposition; reason?: CaptureStopReason }>).detail;
      stopRecognition({
        disposition: detail?.disposition,
        reason: detail?.reason,
      });
    };

    const handleRecordingCleanup = (event: Event) => {
      if (isListeningRef.current || startInProgressRef.current) {
        console.log('🎤 Voice recording cleanup requested (discard)');
        const detail = (event as CustomEvent<{ reason?: CaptureStopReason }>).detail;
        stopRecognition({
          disposition: 'discard',
          reason: detail?.reason || 'navigation',
        });
      }
      releaseMicrophoneStream({ force: true });
    };

    window.addEventListener('startVoiceInput', handleStartVoiceInput);
    window.addEventListener('stopVoiceInput', handleStopVoiceInput);
    window.addEventListener('voice-recording-cleanup', handleRecordingCleanup);

    return () => {
      window.removeEventListener('startVoiceInput', handleStartVoiceInput);
      window.removeEventListener('stopVoiceInput', handleStopVoiceInput);
      window.removeEventListener('voice-recording-cleanup', handleRecordingCleanup);
      stopRecognition({ disposition: 'discard', reason: 'unmount' });
    };
  }, [createRecognitionSession, ensureMicrophoneStream, releaseMicrophoneStream, startMediaRecorderCapture, stopRecognition]);

  return null;
};

export default GlobalVoiceInputHandler;
